/**
 * The held list — prototype VIEWS.review "Held for review" and "Auto-approved log" (docs/prototype.html L1222–1236).
 * Managers see every open held item in Team → Review queue; anyone else sees what they captured, on Capture.
 * Confirm needs a person (POST /held-items/{id}/confirm); Discard and Undo need a reason, which lands in Dropped tasks.
 * An item open for more than 4 hours carries is_late and is flagged.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { DROP_REASON_LABEL, type AutoCreated, type DropReason, type HeldItem } from '@amplispot/contracts';
import { useSession } from '../../lib/session';
import { useClients, useUsers } from '../../lib/data';
import { firstName } from '../../lib/format';
import { whenText } from '../../lib/requests';
import { ago, confTone, useCaptureSettings, useConfirmHeld, useDiscardHeld, useUndoAuto } from '../../lib/capture';
import { Avatar, Empty, ErrorState, Field, Loading, Modal, errorText, useToast } from '../../ui/kit';

/** Only while GET /me/capture-settings loads (the contract default); the colour follows Settings → Thresholds after. */
const THRESHOLD_FALLBACK = 85;

/** `focus`: a held item id to highlight and scroll to (Review queue's `?held=<id>`, the route GET /review-queue gives). */
export function HeldList({ q, compact, focus }: { q: UseQueryResult<HeldItem[]>; compact?: boolean; focus?: string | null }) {
  const { can, me } = useSession();
  const isMgr = can('users.read');
  // `capture.auto_threshold` as configured: green at or above it, the same line the Review queue head states.
  const threshold = useCaptureSettings().data?.auto_threshold ?? THRESHOLD_FALLBACK;
  const clients = useClients();
  const users = useUsers(isMgr);
  const people = useMemo(
    () => (users.data ?? []).filter((u) => u.status === 'active' && ['founder', 'ops_ad', 'ops_member'].includes(u.role)),
    [users.data],
  );
  if (q.isPending) return <Loading />;
  if (q.isError) {
    return (
      <div style={{ padding: 12 }}>
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </div>
    );
  }
  if (!q.data.length) {
    return (
      <Empty title="Queue is clear">
        Nothing is waiting on a person.
      </Empty>
    );
  }
  const self = { id: me.user.id, name: me.user.name };
  return (
    <>
      {q.data.map((h) => (
        <HeldRow
          key={h.id}
          h={h}
          compact={compact}
          focused={h.id === focus}
          threshold={threshold}
          people={isMgr ? people.map((p) => ({ id: p.id, name: p.name })) : [self]}
          clients={(clients.data ?? []).map((c) => ({ id: c.id, name: c.name }))}
        />
      ))}
    </>
  );
}

type Opt = { id: string; name: string };

function HeldRow({
  h,
  people,
  clients,
  compact,
  focused,
  threshold,
}: {
  h: HeldItem;
  people: Opt[];
  clients: Opt[];
  compact?: boolean;
  focused?: boolean;
  threshold: number;
}) {
  const toast = useToast();
  const confirm = useConfirmHeld();
  const [who, setWho] = useState(h.suggested_assignee?.id ?? '');
  const [client, setClient] = useState(h.client?.id ?? '');
  const [discarding, setDiscarding] = useState(false);
  const opts = useMemo(() => {
    const o = [...people];
    if (h.suggested_assignee && !o.some((p) => p.id === h.suggested_assignee!.id)) o.unshift(h.suggested_assignee);
    return o;
  }, [people, h.suggested_assignee]);

  const row = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused) row.current?.scrollIntoView({ block: 'center' });
  }, [focused]);

  return (
    <div
      ref={row}
      className="li"
      data-held={h.id}
      aria-current={focused ? 'true' : undefined}
      style={{ flexWrap: 'wrap', ...(focused ? { background: 'var(--brand-bg)', boxShadow: 'inset 3px 0 0 var(--brand)' } : {}) }}
    >
      <div className="b" style={{ minWidth: compact ? 200 : 260 }}>
        <div className="t">{h.raw_text || 'The recording could not be turned into text'}</div>
        <div className="m">
          <span>
            From {firstName(h.by.name)}, {heldAt(h.at)}
          </span>
          <span className={`conf ${confTone(h.confidence, threshold)}`}>{h.confidence}%</span>
          <span className="chip warm">{h.why_held.charAt(0).toUpperCase() + h.why_held.slice(1)}</span>
          {h.is_late ? <span className="chip cold">Waiting {Math.floor(h.age_hours)}h</span> : null}
          {h.client_guess && !h.client ? <span>Heard “{h.client_guess}”</span> : null}
        </div>
        <div className="split" style={{ marginTop: 8, flexWrap: 'wrap' }}>
          <select className="in" style={{ width: 'auto' }} value={who} onChange={(e) => setWho(e.target.value)} aria-label="Who does it">
            {!who ? <option value="">Pick a person</option> : null}
            {opts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select className="in" style={{ width: 'auto' }} value={client} onChange={(e) => setClient(e.target.value)} aria-label="Client">
            <option value="">No client</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="split" style={{ alignSelf: 'center' }}>
        <button className="btn sm bad" onClick={() => setDiscarding(true)}>
          Discard
        </button>
        <button
          className="btn sm good"
          disabled={confirm.isPending}
          onClick={() => {
            if (!who) {
              toast('Pick who does it first');
              return;
            }
            confirm.mutate(
              { id: h.id, body: { assignee_id: who, client_id: client || null } },
              {
                onSuccess: () => toast(`Confirmed. Task created for ${firstName(opts.find((p) => p.id === who)?.name ?? 'them')}`),
                onError: (e) => toast(errorText(e)),
              },
            );
          }}
        >
          Confirm
        </button>
      </div>
      {discarding ? <ReasonModal title="Discard this item" sub={h.raw_text} onClose={() => setDiscarding(false)} kind="discard" id={h.id} /> : null}
    </div>
  );
}

/** Why it is left out — Discard (held) and Undo (auto-created) both log a Dropped tasks row with the reason. */
function ReasonModal({ title, sub, onClose, kind, id }: { title: string; sub: string; onClose: () => void; kind: 'discard' | 'undo'; id: string }) {
  const toast = useToast();
  const discard = useDiscardHeld();
  const undo = useUndoAuto();
  const [reason, setReason] = useState<DropReason | ''>('');
  const [note, setNote] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const busy = discard.isPending || undo.isPending;
  const save = () => {
    if (!reason) return setErrs({ reason_code: 'Pick a reason' });
    if (reason === 'other' && !note.trim()) return setErrs({ note: 'Say what the reason is when you pick Other' });
    const body = { reason_code: reason, ...(note.trim() ? { note: note.trim() } : {}) };
    const done = {
      onSuccess: () => {
        toast(kind === 'discard' ? 'Discarded, and logged in Dropped tasks.' : 'Undone. Task removed.');
        onClose();
      },
      onError: (e: unknown) => toast(errorText(e)),
    };
    if (kind === 'discard') discard.mutate({ id, body }, done);
    else undo.mutate({ taskId: id, body }, done);
  };
  return (
    <Modal
      title={title}
      sub={sub}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={busy}>
            {kind === 'discard' ? 'Discard' : 'Undo'}
          </button>
        </>
      }
    >
      <Field label="Why" error={errs.reason_code}>
        <select className="in" value={reason} onChange={(e) => setReason(e.target.value as DropReason)}>
          <option value="">Pick a reason</option>
          {(Object.entries(DROP_REASON_LABEL) as [DropReason, string][]).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Note" error={errs.note}>
        <textarea className="in" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

/** Review queue → "Auto-approved log": what the countdown created on its own, each with Undo. */
export function AutoLogTable({ q }: { q: UseQueryResult<{ data: AutoCreated[] }> }) {
  const [undoing, setUndoing] = useState<AutoCreated | null>(null);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data.data.length) return <Empty title="Nothing approved on its own yet">Tasks the countdown creates appear here, with Undo.</Empty>;
  const now = Date.now();
  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Task</th>
            <th>Client</th>
            <th>Assignee</th>
            <th>Confidence</th>
            <th>When</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {q.data.data.map((a) => (
            <tr key={a.task_id}>
              <td>
                <b>{a.title}</b>
                <div className="sub">From {firstName(a.by.name)}</div>
              </td>
              <td>{a.client?.name ?? 'No client'}</td>
              <td>{a.assignee ? <Avatar user={{ name: a.assignee.name, initials: initialsOf(a.assignee.name) }} /> : '—'}</td>
              <td>
                <span className="conf h">{a.confidence}%</span>
              </td>
              <td className="sub" style={{ color: 'var(--sub)' }}>
                {ago(a.created_at, now)}
              </td>
              <td className="tr">
                {a.undone ? (
                  <span className="chip">Undone</span>
                ) : (
                  <button className="btn sm" onClick={() => setUndoing(a)}>
                    Undo
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {undoing ? <ReasonModal title="Undo this task" sub={undoing.title} kind="undo" id={undoing.task_id} onClose={() => setUndoing(null)} /> : null}
    </>
  );
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

/** Prototype "From <first name>, 8:41 am": the clock time today, "yesterday 5:30 pm" / "3 Oct 4:05 pm" before that. */
function heldAt(ts: string): string {
  const w = whenText(ts);
  return w.startsWith('Today ') ? w.slice(6) : w.replace(/^Yesterday/, 'yesterday');
}
